<template>
  <template v-if="isMissingSettings">
    <DashboardMissingSettings />
  </template>
  <template v-else-if="!filters.submittedMeta.hasGeographicalFilter">
    <DashboardMissingFilter />
  </template>
  <template v-else-if="isMissingData">
    <DashboardMissingData />
  </template>
  <template v-else>
    <DashboardDataSection
      :is-loading="false"
      :is-missing-data="false"
      :is-missing-filter="false"
      :data-sources="dataSources"
      :title="t('dashboard__text__chart-graphs')"
      :children="sections"
    />
    <section :class="!mobileScreen ? 'graph-container' : 'graph-container-mobile'">
      <div
        v-for="(plot, type) in plotMap"
        :key="type"
        class="graph"
        :class="{ fullscreen: sharedData?.fullscreen && !mobileScreen }"
      >
        <component
          :is="plot.is"
          :key="sharedData?.fullscreen"
          :isMissingRoom:="isMissingRoom"
          :settings="plots?.find((p) => p.type === type)"
          v-bind="plot.props"
        />
      </div>
    </section>
  </template>
  <DashboardLegend
    v-if="filters.submittedMeta.hasGeographicalFilter && !isMissingData"
    :fullscreen="sharedData?.fullscreen"
  />
</template>
<script lang="ts" setup>
import DashboardMissingSettings from './DashboardMissingSettings.vue'
import DashboardMissingFilter from './DashboardMissingFilter.vue'
import DashboardMissingData from './DashboardMissingData.vue'
import DashboardLegend from '../components/DashboardLegend.vue'
import { useRealm } from '@/composables/useRealm'
import { computed, inject, onMounted } from 'vue'
import { usePlotComponentMap } from '../composables/usePlotComponentMap'
import { sharedDataKey } from '../../types'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { FilterQueryDefault } from '@/api-client/models/shared/Contracts'
import { FeatureType, useFilters } from '@/composables/useFilters'
import { useDataFetcher } from '@/composables/useDataFetcher'
import * as utilization from '@/api-client/models/utilization/Contracts'
import * as indoorclimate from '@/api-client/models/indoorclimate/Contracts'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import DashboardDataSection, { Section } from './DashboardDataSection.vue'
import { DataSource } from './data-section-types'
import { Chart } from './ChartList.vue'
import { bucketSizeToFormat } from './ChartComponent/utils'
import { formatter } from '../formatter'
import { LineChartValue } from './ChartComponent/chart/lineChart'

type Props = {
  title: LocaleString
  info?: LocaleString
  graphs?: any
  isMissingData: boolean
}

defineProps<Props>()

const timeseries = useDataFetcher('fetch.utilization.timeseries')
const quota = useDataFetcher('fetch.utilization.quota')
const toplist = useDataFetcher('fetch.utilization.toplist', {
  fetchOnlyIf: (query: utilization.ToplistQuery | null) => isToplistEnabled(query?.filter)
})
const realm = useRealm()
const filters = useFilters(FeatureType.Utilization)
const { t, locale } = useI18n()
const submitted = computed(() => (filters.submitted === null ? undefined : filters.submitted))

const isMissingRoom = computed(() => {
  if (submitted.value?.room === undefined) {
    return false
  }
  return true
})
function toTimeSeries(
  id: string,
  converter: (value: utilization.TimeSeriesValue, data: utilization.TimeSeriesResponse) => number
): DataSource {
  const query = timeseries.query.value
  const data = timeseries.data.value
  if (data === undefined || query === undefined) {
    return {
      id,
      type: 'value',
      value: undefined
    }
  }
  const response: indoorclimate.TimeSeries = {
    aggregation: {
      bucketSize: query.resolution as indoorclimate.BucketSize
    } as indoorclimate.Aggregation,
    splits: [
      {
        numberOf: {},
        values: data.values.map((v) => ({
          timestamp: v.date,
          value: converter(v, timeseries.data.value!)
        }))
      }
    ]
  }
  return {
    id,
    type: 'value',
    value: response
  }
}

function toBarChart(
  id: string,
  converter: (value: utilization.TimeSeriesValue, data: utilization.TimeSeriesResponse) => number
): DataSource<'value'> {
  if (!timeseries.data.value) {
    return {
      id,
      type: 'value',
      value: undefined
    }
  }
  const format = bucketSizeToFormat[timeseries.data.value.resolution]
  const values = timeseries.data.value.values.map((v) => ({
    label: formatter(v.date, locale.value, format),
    height: converter(v, timeseries.data.value!)
  }))
  return {
    id,
    type: 'value',
    value: values
  }
}

const calculateUsageAsPercentage = (
  data: utilization.QuotaResponse['usage' | 'booking'],
  totalSpaces: number,
  totalHours: number
) => {
  return data
    .reduce((prev, curr) => {
      const prevNumberOfHours = prev.length == 0 ? 0 : prev[prev.length - 1].numberOfHours
      return [
        ...prev,
        {
          numberOfHours: prevNumberOfHours + curr.numberOfHours,
          numberOfSpaces: curr.numberOfSpaces
        }
      ]
    }, [] as Array<{ numberOfHours: number; numberOfSpaces: number }>)
    .map(({ numberOfSpaces, numberOfHours }) => ({
      numberOfSpaces: numberOfSpaces / totalSpaces,
      numberOfHours: numberOfHours / totalHours
    }))
}

const calculateUsage = (usageAsPercentage: ReturnType<typeof calculateUsageAsPercentage>) => {
  return [...Array(101).keys()].map((percentage) => {
    const u =
      usageAsPercentage.find(({ numberOfHours }) => numberOfHours > percentage / 100)
        ?.numberOfSpaces ?? 0
    return Math.floor(u * 100)
  })
}

const createDataPoints = (usage: ReturnType<typeof calculateUsage>): { x: number; y: number }[] => {
  return usage.map((datapoint, i) => ({ x: i, y: datapoint }))
}

const populateDataset = (
  data: utilization.QuotaResponse,
  onlyBookable?: boolean
): LineChartValue[][] => {
  const usageAsPercentage = calculateUsageAsPercentage(
    data.usage,
    data.numberOfSpaces,
    data.numberOfHours
  )
  const usageAsPercentageBookable = calculateUsageAsPercentage(
    data.booking,
    data.numberOfSpaces,
    data.numberOfHours
  )

  const usage = calculateUsage(usageAsPercentage)
  const usageBooking = calculateUsage(usageAsPercentageBookable)

  const usageArray = createDataPoints(usage)
  const bookingArray = createDataPoints(usageBooking)

  return [usageArray, ...(onlyBookable ? [bookingArray] : [])]
}

function toLineChart(id: string, index: number): DataSource {
  if (isMissingRoom.value) {
    return {
      id,
      type: 'value',
      value: undefined
    }
  }
  if (!quota.data.value!) {
    return {
      id,
      type: 'value',
      value: undefined
    }
  }
  const dataset = populateDataset(quota.data.value!, filters.submitted?.onlyBookable)
  return {
    id,
    type: 'value',
    value: dataset[index] ?? []
  }
}

const dataSources = computed<DataSource[]>((): DataSource[] => {
  const showBookingData = timeseries.query.value?.filter.onlyBookable === true
  const usageAverage = toTimeSeries(
    'usageAverage',
    (v, data) => ((v.used + v.bookedAndUsed) / (v.numberOfHours * data.numberOfSpaces)) * 100
  )
  const usageMax = toTimeSeries('usageMax', (v, data) => (v.maxUsed / data.numberOfSpaces) * 100)
  const bookingAverage = toTimeSeries(
    'bookingAverage',
    (v, data) => ((v.booked + v.bookedAndUsed) / (v.numberOfHours * data.numberOfSpaces)) * 100
  )
  const bookingMax = toTimeSeries(
    'bookingMax',
    (v, data) => (v.maxBooked / data.numberOfSpaces) * 100
  )

  const bars: DataSource<'value'>[] = !showBookingData
    ? [
        toBarChart(
          'usageHistogram',
          (v, data) => ((v.used + v.bookedAndUsed) / (v.numberOfHours * data.numberOfSpaces)) * 100
        )
      ]
    : [
        toBarChart(
          'onlyUsedHistogram',
          (v, data) => (v.used / (v.numberOfHours * data.numberOfSpaces)) * 100
        ),
        toBarChart(
          'bookedAndUsedHistogram',
          (v, data) => (v.bookedAndUsed / (v.numberOfHours * data.numberOfSpaces)) * 100
        ),
        toBarChart(
          'onlyBookedHistogram',
          (v, data) => (v.booked / (v.numberOfHours * data.numberOfSpaces)) * 100
        )
      ]

  const barSums = Array.from({ length: bars[0].value?.length ?? 0 }, () => [] as number[])
  barSums.forEach((_, index) => {
    bars.forEach((bar) => {
      barSums[index].push(bar.value[index].height)
    })
  })

  const usedCapacityPerTimeUnit = toLineChart('usedCapacityPerTimeUnit', 0)
  const bookedCapacityPerTimeUnit = toLineChart('bookedCapacityPerTimeUnit', 1)

  return [
    usageAverage,
    usageMax,
    bookingAverage,
    bookingMax,
    ...bars.concat({
      id: 'notUsedHistogram',
      type: 'value',
      value: barSums.map((value, index) => ({
        label: bars[0].value![index].label,
        height: 100 - value.reduce((a, b) => a + b, 0)
      }))
    }),
    usedCapacityPerTimeUnit,
    bookedCapacityPerTimeUnit
  ]
})

const sections = computed<Section[]>((): Section[] => {
  const showBookingData = timeseries.query.value?.filter.onlyBookable === true

  const overviewSettings = realm.settings?.config?.utilization.plots.find(
    (p) => p.type === 'OVERVIEW'
  )
  const usageAndBookingOverTimeSettings = realm.settings?.config?.utilization.plots.find(
    (p) => p.type === 'USAGE_AND_BOOKING_OVER_TIME'
  )

  const usedCapacityPerTimeUnitSettings = realm.settings?.config?.utilization.plots.find(
    (p) => p.type === 'USED_CAPACITY_PER_TIME_UNIT'
  )

  const cards: Chart[] = [
    {
      name: 'overview',
      title: overviewSettings?.title ?? { sv: 'Översikt', en: 'Overview' },
      info: overviewSettings?.info,
      props: {
        type: 'timeseries',
        dataSources: showBookingData
          ? ['usageAverage', 'usageMax', 'bookingAverage', 'bookingMax']
          : ['usageAverage', 'usageMax'],
        lineFormats: [
          { color: '#40A9FF', style: 'solid' },
          { color: '#40A9FF', style: 'dashed' },
          { color: '#9254DE', style: 'solid' },
          { color: '#9254DE', style: 'dashed' }
        ],
        gradientBelow: true,
        dataFormat: {
          format: {
            type: 'intl-number',
            maximumFractionDigits: 1
          },
          steps: [{ format: { type: 'format-string', format: '%s%' } }]
        },
        dataTicks: {
          values: [0, 50, 100]
        },
        missingDataMessage: {
          en: 'Missing data',
          sv: 'Saknar data'
        },
        labels: showBookingData
          ? [
              { sv: 'Använding', en: 'Usage' },
              { sv: 'Max användning', en: 'Max usage' },
              { sv: 'Bokning', en: 'Booking' },
              { sv: 'Max bokning', en: 'Max booking' }
            ]
          : [
              { sv: 'Använding', en: 'Usage' },
              { sv: 'Max användning', en: 'Max usage' }
            ]
      }
    },
    {
      name: 'usageAndBookingOverTime',
      title: usageAndBookingOverTimeSettings?.title ?? {
        sv: 'Användning och bokning',
        en: 'Usage and booking'
      },
      info: usageAndBookingOverTimeSettings?.info,
      props: {
        type: 'bar',
        codomainFormat: {
          format: {
            type: 'intl-number',
            maximumFractionDigits: 1
          },
          steps: [{ format: { type: 'format-string', format: '%s%' } }]
        },
        codomainTicks: {
          values: [0, 50, 100]
        },
        verticalAxis: { position: 'left' },
        missingDataMessage: {
          en: 'Missing data',
          sv: 'Saknar data'
        },
        dataSources: showBookingData
          ? [
              'onlyUsedHistogram',
              'bookedAndUsedHistogram',
              'onlyBookedHistogram',
              'notUsedHistogram'
            ]
          : ['usageHistogram', 'notUsedHistogram'],
        barFormats: showBookingData
          ? [
              {
                label: { en: 'Used, not booked', sv: 'Använt, ej bokat' },
                color: '#40A9FF'
              },
              {
                label: { en: 'Used, booked', sv: 'Använt, bokat' },
                color: '#40A9FF',
                pattern: {
                  type: 'diagonal-stripes',
                  color: '#9254DE',
                  background: '#40A9FF'
                }
              },
              {
                label: { en: 'Booked, not used', sv: 'Bokat, ej använt' },
                color: 'transparent',
                pattern: {
                  type: 'diagonal-stripes',
                  color: '#9254DE'
                }
              },
              {
                label: { en: 'Unused', sv: 'Ej använt' },
                color: '#E5E5E5'
              }
            ]
          : [
              {
                label: { en: 'Usage', sv: 'Använt' },
                color: '#40A9FF'
              },
              {
                label: { en: 'Unused', sv: 'Ej använt' },
                color: '#E5E5E5'
              }
            ],
        legendColor: '#40A9FF'
      }
    },
    {
      name: 'usedCapacityPerTimeUnit',
      title: usedCapacityPerTimeUnitSettings?.title ?? {
        sv: 'Användning över tid',
        en: 'Usage over time'
      },
      info: usedCapacityPerTimeUnitSettings?.info,
      props: {
        type: 'line',
        dataSources: ['usedCapacityPerTimeUnit', 'bookedCapacityPerTimeUnit'],
        lineFormats: [
          { color: '#40A9FF', style: 'solid' },
          { color: '#9254DE', style: 'solid' }
        ],
        gradientBelow: true,
        xFormat: {
          format: {
            type: 'intl-number',
            maximumFractionDigits: 1
          },
          steps: [{ format: { type: 'format-string', format: '%s%' } }]
        },
        missingDataMessage: {
          en: 'Missing data',
          sv: 'Saknar data'
        },
        xTooltipFormat: {
          format: {
            type: 'intl-number',
            maximumFractionDigits: 1
          },
          steps: [
            {
              format: {
                type: 'locale-format-string',
                format: {
                  en: '%s% of time',
                  sv: '%s% av tiden'
                }
              }
            }
          ]
        },
        yFormat: {
          format: {
            type: 'intl-number',
            maximumFractionDigits: 1
          },
          steps: [{ format: { type: 'format-string', format: '%s%' } }]
        },
        xTicks: {
          values: [0, 50, 100]
        },
        yTicks: {
          values: [0, 50, 100]
        },
        labels: [
          { sv: 'Använding', en: 'Usage' },
          { sv: 'Bokning', en: 'Booking' }
        ]
      }
    }
  ]
  return [
    {
      name: 'charts',
      title: { sv: 'Graf', en: 'Graph' },
      info: { sv: 'Graf', en: 'Graph' },
      children: cards
    }
  ]
})
const plotMap = usePlotComponentMap(toplist, submitted)
const store = useStore()
const mobileScreen = computed<boolean>(() => store.state.mobileScreen)
const sharedData = inject(sharedDataKey)
const plots = computed(() => realm.settings?.config?.utilization.plots)

onMounted(() => {
  sharedData?.updateSectionIndex(1)
})

function isToplistEnabled(filter?: FilterQueryDefault): boolean {
  if (!filter) {
    return false
  }
  if (['CONSTRUCTION', 'LEVEL'].includes(filter.entityType) && filter.entityIds.length === 1) {
    return true
  }
  return false
}

const isMissingSettings = computed(() => {
  if (!plots.value && !realm.error) {
    return true
  }
  return false
})
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';
.graph-section-header-text {
  @include font-mixins.desktop-bold-paragraph;
  color: black;
}
.graph-section-header {
  display: flex;
  padding: 2px;
  margin-top: 16px;
  margin-bottom: 16px;
  align-content: flex-start;
}
.graph-container {
  flex-grow: 1;
  height: min-content;
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  gap: calc(variables.$gap-large * 2);
  row-gap: calc(variables.$gap-large * 2);
  margin-bottom: 24px;
  margin-top: 16px;
}
.graph-container-mobile {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
  margin-bottom: 16px;
}
.graph {
  width: 100%;
  background-color: variables.$color-pure-white;
  border-radius: 8px;
  &.fullscreen {
    width: calc(50% - variables.$gap-large);
  }
}
</style>
